import { DeleteOutlined, EditFilled } from "@ant-design/icons";
import { Button, Pagination, Table } from "antd";
import {
  TableActionButtons,
  buildColumnsWithHeaderFilters,
} from "../../../Components";
import { PlusIcon } from "../../../assets/icon";
import { decorateEditableColumns } from "../../../shared/helpers/editableColumns";
import { checkPermissions } from "../../../utils/checkPermissions";
import styles from "../FreightCosts.module.scss";

type FreightCostGroupTabProps = {
  dataSource: any[];
  editIndex: any[];
  features: any;
  filters: any;
  onAddRow: () => void;
  onActionCancel: (index: string | number) => void;
  onActionSaveGroup: (index: string | number, rowIndex: number) => void;
  isSavePending: (index: string | number) => boolean;
  onActionTable: (action: string, record: any, index: string) => void;
  pagination: {
    total: number;
    current: number;
    pageSize: number;
  };
  fetchTruckVendorOptions: (value: string) => Promise<any[]>;
  onPaginationChange: (page: number, pageSize?: number) => void;
  onUpdateRow: (key: string, value: any, index: string | number) => void;
  setFilters: (value: any) => void;
};

const FreightCostGroupTab = ({
  dataSource,
  editIndex,
  features,
  filters,
  onAddRow,
  onActionCancel,
  onActionSaveGroup,
  isSavePending,
  onActionTable,
  pagination,
  fetchTruckVendorOptions,
  onPaginationChange,
  onUpdateRow,
  setFilters,
}: FreightCostGroupTabProps) => {
  const columns = buildColumnsWithHeaderFilters(
    decorateEditableColumns(
      [
      {
        title: "#",
        dataIndex: "_id",
        width: 50,
        fixed: "left",
        hidden: false,
        render: (_row: any, _record: any, index: number) => index + 1,
      },
      {
        title: "Tên nhóm",
        dataIndex: "groupName",
        fixed: false,
        hidden: false,
        filterConfig: {
          type: "string",
          filterProps: {
            placeholder: "Nhập tên nhóm",
          },
        },
        editType: "Input",
      },
      {
        title: "Nhà thầu",
        dataIndex: "truckVendors",
        fixed: false,
        hidden: false,
        filterConfig: {
          type: "string",
          filterProps: {
            placeholder: "Nhập nhà thầu",
          },
        },
        editType: "DebounceSelect",
        colReturn: (cellValue: any) =>
          cellValue && cellValue?.length > 0
            ? cellValue.map((it: any) => it?.shortName ?? it.label).join(", ")
            : "",
        editProps: (_cellValue: any, record: any) => ({
          value: editIndex
            .find((edit: any) => edit.index === record._id)
            ?.record?.truckVendors?.map((it: any) => ({
              label: it.shortName,
              value: it?._id,
            })),
          mode: "multiple",
          fetchOptions: fetchTruckVendorOptions,
        }),
      },
      {
        title: "Tác vụ",
        dataIndex: "actions",
        fixed: false,
        hidden: false,
        render: (_row: string, record: any, index: number) => {
          const savePending = isSavePending(record._id);
          const saveAction = record.status === "new" ? "add" : "edit";
          const canSave = checkPermissions(
            "CONTRACTOR_FREIGHT_GROUP",
            saveAction,
            features,
          );
          return editIndex.find((ind: any) => ind.index === record._id) ? (
            <div>
              <Button
                loading={savePending}
                disabled={savePending || !canSave}
                onClick={() => {
                  onActionSaveGroup(record._id, index);
                }}
              >
                Lưu
              </Button>{" "}
              <Button
                disabled={savePending}
                onClick={() => {
                  onActionCancel(record._id);
                }}
              >
                Hủy
              </Button>
            </div>
          ) : (
            <TableActionButtons
              items={[
                {
                  key: "edit",
                  label: "Chỉnh sửa",
                  icon: <EditFilled />,
                  tone: "edit",
                  disabled: !checkPermissions(
                    "CONTRACTOR_FREIGHT_GROUP",
                    "edit",
                    features,
                  ),
                  onClick: () => onActionTable("edit", record, record._id),
                },
                {
                  key: "delete",
                  label: "Xoá",
                  icon: <DeleteOutlined />,
                  tone: "delete",
                  danger: true,
                  disabled: !checkPermissions(
                    "CONTRACTOR_FREIGHT_GROUP",
                    "delete",
                    features,
                  ),
                  onClick: () => onActionTable("delete", record, record._id),
                },
              ]}
            />
          );
        },
      },
      ],
      {
        editIndex,
        onUpdate: (dataIndex, value, rowId) =>
          onUpdateRow(dataIndex, value, rowId),
      },
    ),
    filters,
    setFilters,
  );

  return (
    <div>
      <Table
        size="small"
        dataSource={dataSource}
        columns={columns}
        scroll={{ x: "max-content" }}
        bordered
        style={{ width: "100%" }}
        pagination={false}
      />
      <Button
        className={styles.ButtonPlus}
        icon={<PlusIcon />}
        onClick={onAddRow}
        disabled={!checkPermissions("CONTRACTOR_FREIGHT_GROUP", "add", features)}
      >
        Thêm mới
      </Button>
      <Pagination
        total={pagination.total}
        current={pagination.current}
        pageSize={pagination.pageSize}
        onChange={onPaginationChange}
        showSizeChanger
        align="end"
        className={styles.Pagination}
      />
    </div>
  );
};

export { FreightCostGroupTab };

